import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
    GasBannerAlert,
    GasButton,
    GasEmptyState,
    useGasI18n,
    useGasNotify,
} from '../../shared/kit';
import {
    configureApi,
    constrainedSearch,
    getTelemetrySession,
    setTelemetrySession,
    telemetryRun,
} from './api';
import { InterpretationPanel } from './components/InterpretationPanel';
import { SolutionCard } from './components/SolutionCard';
import type {
    ConstrainedSearchInitData,
    ConstrainedSearchResponse,
    UiPhase,
} from './types';

export default function App({ initData }: { initData: ConstrainedSearchInitData }) {
    const { t } = useGasI18n();
    const notify = useGasNotify();
    const inputId = useId();
    const liveId = useId();
    const inputRef = useRef<HTMLTextAreaElement | null>(null);

    const [query, setQuery] = useState('');
    const [phase, setPhase] = useState<UiPhase>('idle');
    const [response, setResponse] = useState<ConstrainedSearchResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [latencyMs, setLatencyMs] = useState<number | null>(null);
    const requestSeq = useRef(0);

    const examples = initData.examples?.length
        ? initData.examples
        : [
            t('location.search.example_cameras_pole'),
            t('location.search.example_cameras_dates'),
            t('location.search.example_weekend'),
        ];
    const locale = initData.locale || 'fr';
    const timezone = initData.timezone
        || (typeof Intl !== 'undefined'
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : 'Europe/Zurich');

    useEffect(() => {
        configureApi(initData.restBase, initData.nonce);
    }, [initData.restBase, initData.nonce]);

    const runSearch = useCallback(async (text: string) => {
        const q = text.trim();
        if (q.length < 2) {
            setError(t('location.search.error.query_short'));
            setPhase('error');
            return;
        }
        const seq = ++requestSeq.current;
        setPhase('loading');
        setError(null);
        const started = performance.now();
        try {
            const res = await constrainedSearch({
                query: q,
                locale,
                timezone,
            });
            if (seq !== requestSeq.current) return;

            const elapsed = Math.round(performance.now() - started);
            setLatencyMs(elapsed);
            setResponse(res);

            if (res.status === 'denied') {
                setPhase('denied');
                setError(res.deny_reason || t('location.search.error.denied'));
                return;
            }

            setPhase('ready');

            // Télémétrie run + impressions (consent géré côté serveur).
            const impressions = (res.results || []).flatMap((sol, si) =>
                (sol.allocations || []).slice(0, 10).map((a, ai) => ({
                    object_type: 'rental_item',
                    object_id: a.item_id,
                    rank: si * 10 + ai + 1,
                    score: a.score ?? null,
                })),
            );

            const tel = await telemetryRun({
                query_id: res.query_id,
                surface: initData.surface || 'location_constrained_search',
                experience: res.experience_key || initData.experience || 'location.catalog',
                profile_version: res.profile || 'constrained_search',
                locale,
                query: q,
                filters: res.interpretation?.filters ?? null,
                result_count: (res.results || []).reduce(
                    (n, s) => n + (s.allocations?.length || 0),
                    0,
                ),
                latency_ms: elapsed,
                path: res.meta?.path || 'deterministic',
                session_token: getTelemetrySession(),
                impressions,
            });
            if (tel?.session_token) setTelemetrySession(tel.session_token);
        } catch (e) {
            if (seq !== requestSeq.current) return;
            const msg = (e as Error).message || t('errors.generic');
            setError(msg);
            setPhase('error');
            notify.error(msg);
        }
    }, [initData.experience, initData.surface, locale, notify, t, timezone]);

    function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        void runSearch(query);
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        // Entrée seule = soumettre ; Shift+Entrée = nouvelle ligne (mobile-friendly).
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void runSearch(query);
        }
    }

    const primary = response?.results?.find((r) => r.kind !== 'alternative')
        || response?.results?.[0]
        || null;
    const alternatives = (response?.results || []).filter((r) => r.kind === 'alternative');

    const isAssistant = initData.variant === 'assistant';
    const HeadingTag = isAssistant ? 'h2' : 'h1';

    return (
        <div className={`gas-loc-csearch${isAssistant ? ' gas-loc-csearch--assistant' : ''}`}>
            <header className="gas-loc-csearch__header">
                <HeadingTag className="gas-loc-csearch__title">
                    {isAssistant ? t('location.search.assistant_title') : t('location.search.title')}
                </HeadingTag>
                <p className="gas-loc-csearch__intro">
                    {isAssistant ? t('location.search.assistant_intro') : t('location.search.intro')}
                </p>
            </header>

            <form className="gas-loc-csearch__form" onSubmit={onSubmit} noValidate>
                <label className="gas-loc-csearch__label" htmlFor={inputId}>
                    {t('location.search.query_label')}
                </label>
                <textarea
                    ref={inputRef}
                    id={inputId}
                    className="gas-loc-csearch__input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={3}
                    enterKeyHint="search"
                    autoComplete="off"
                    spellCheck
                    placeholder={t('location.search.placeholder')}
                    aria-describedby={liveId}
                    aria-busy={phase === 'loading'}
                    disabled={phase === 'loading'}
                />
                <div className="gas-loc-csearch__form-actions">
                    <GasButton
                        type="submit"
                        variant="primary"
                        disabled={phase === 'loading' || query.trim().length < 2}
                    >
                        {phase === 'loading'
                            ? t('location.search.searching')
                            : t('location.search.submit')}
                    </GasButton>
                    {query ? (
                        <GasButton
                            type="button"
                            variant="ghost"
                            disabled={phase === 'loading'}
                            onClick={() => {
                                requestSeq.current += 1;
                                setQuery('');
                                setResponse(null);
                                setError(null);
                                setPhase('idle');
                                inputRef.current?.focus();
                            }}
                        >
                            {t('common.clear')}
                        </GasButton>
                    ) : null}
                </div>
            </form>

            {examples.length > 0 && phase === 'idle' && !response ? (
                <div className="gas-loc-csearch__examples">
                    <p className="gas-loc-csearch__examples-label">{t('location.search.examples')}</p>
                    <ul>
                        {examples.map((ex) => (
                            <li key={ex}>
                                <button
                                    type="button"
                                    className="gas-loc-csearch__example"
                                    onClick={() => {
                                        setQuery(ex);
                                        void runSearch(ex);
                                    }}
                                >
                                    {ex}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            <div id={liveId} className="gas-loc-csearch__live" aria-live="polite" aria-atomic="true">
                {phase === 'loading' ? t('location.search.searching') : null}
                {phase === 'ready' && primary
                    ? t('location.search.live_result', {
                        status: t(
                            primary.status === 'full'
                                ? 'location.search.status.full'
                                : primary.status === 'partial'
                                    ? 'location.search.status.partial'
                                    : 'location.search.status.none',
                        ),
                    })
                    : null}
            </div>

            {error && phase !== 'ready' ? (
                <GasBannerAlert
                    type={phase === 'denied' ? 'warning' : 'danger'}
                    title={phase === 'denied'
                        ? t('location.search.error.denied_title')
                        : t('location.search.error.title')}
                    message={error}
                />
            ) : null}

            {phase === 'ready' && response ? (
                <div className="gas-loc-csearch__results">
                    <InterpretationPanel
                        interpretation={response.interpretation}
                        locale={locale}
                    />

                    {response.query_id ? (
                        <p className="gas-loc-csearch__query-id" data-query-id={response.query_id}>
                            <span className="gas-loc-csearch__label">{t('location.search.query_id')}</span>
                            {' '}
                            <code>{response.query_id}</code>
                            {latencyMs != null ? (
                                <span className="gas-loc-csearch__latency">
                                    {' · '}
                                    {t('location.search.latency_ms', { n: String(latencyMs) })}
                                </span>
                            ) : null}
                        </p>
                    ) : null}

                    {response.meta?.mutates === true ? (
                        <GasBannerAlert
                            type="danger"
                            title={t('location.search.error.mutation_blocked')}
                            message={t('location.search.error.mutation_blocked_body')}
                        />
                    ) : null}

                    {primary ? (
                        <SolutionCard
                            solution={primary}
                            queryId={response.query_id}
                            cartUrl={initData.cartUrl}
                            rank={1}
                        />
                    ) : (
                        <GasEmptyState
                            title={t('location.search.empty_title')}
                            message={t('location.search.empty_body')}
                        />
                    )}

                    {alternatives.map((alt, i) => (
                        <SolutionCard
                            key={`alt-${i}`}
                            solution={alt}
                            queryId={response.query_id}
                            cartUrl={initData.cartUrl}
                            rank={i + 2}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
